import {Fragment, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import {useAppDispatch, useAppSelector} from "../store/hooks";
import {fetchPlans, type Plan, plansSelectors} from "../store/plansSlice";
import {PageHeader} from "../components/ui/PageHeader";
import {StatusChip} from "../components/ui/StatusChip";
import {colors} from "../theme/tokens";
import {formatEuroCents} from "../util/format";

type Order = "asc" | "desc";
type SortColumn = "customerCanPurchase" | "name";

/** A sample team size to compare plans by; the backend prices a month as base + per-employee price × employees. */
const SAMPLE_EMPLOYEES = 5;

function hasEmployeePrice(plan: Plan): boolean {
    return (plan.monthlyPricePerEmployeeInCents ?? 0) > 0;
}

export function PlansPage() {
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const plans = useAppSelector(plansSelectors.selectAll);
    const status = useAppSelector((s) => s.plans.status);
    const error = useAppSelector((s) => s.plans.error);

    const [search, setSearch] = useState("");
    const [orderBy, setOrderBy] = useState<SortColumn>("name");
    const [order, setOrder] = useState<Order>("asc");
    const [expandedId, setExpandedId] = useState<string | null>(null);

    useEffect(() => {
        document.title = t("PLANS_PAGE_TITLE");
    }, [t]);

    useEffect(() => {
        if (status === "idle") void dispatch(fetchPlans());
    }, [dispatch, status]);

    const filteredAndSorted = useMemo(() => {
        const query = search.trim().toLowerCase();
        const list = query ? plans.filter((plan) => plan.name.toLowerCase().includes(query)) : [...plans];
        return [...list].sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];
            if (aValue === bValue) return 0;
            if (aValue == null) return 1;
            if (bValue == null) return -1;
            const compared = typeof aValue === "string" ? aValue.localeCompare(String(bValue)) : Number(aValue) - Number(bValue);
            return order === "asc" ? compared : -compared;
        });
    }, [plans, search, orderBy, order]);

    const handleSort = (column: SortColumn) => {
        setOrder(orderBy === column && order === "asc" ? "desc" : "asc");
        setOrderBy(column);
    };

    const sortHeader = (column: SortColumn, label: string) => (
            <TableCell sortDirection={orderBy === column ? order : false}>
                <TableSortLabel active={orderBy === column} direction={orderBy === column ? order : "asc"} onClick={() => handleSort(column)}>
                    {label}
                </TableSortLabel>
            </TableCell>
    );

    const loading = status === "loading" && plans.length === 0;

    return (
            <>
                <PageHeader title={t("NAV_PLANS")} subtitle={t("PLANS_SUBTITLE")} />
                {error && <Alert severity="error">{error}</Alert>}
                <Box sx={{display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2}}>
                    <TextField
                            size="small"
                            placeholder={t("PLANS_SEARCH_PLACEHOLDER")}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            sx={{width: 320}}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{fontSize: 18, color: colors.faint}} />
                                            </InputAdornment>
                                    ),
                                },
                                htmlInput: {"aria-label": t("PLANS_SEARCH_PLACEHOLDER")},
                            }}
                    />
                    <Box sx={{flex: 1}} />
                    <Typography variant="body2" sx={{color: colors.faint}}>{t("PLANS_READ_ONLY_HINT")}</Typography>
                </Box>
                <Paper variant="outlined" sx={{overflow: "hidden"}}>
                    <TableContainer>
                        <Table aria-label={t("PLANS_TABLE_ARIA")}>
                            <TableHead>
                                <TableRow>
                                    {sortHeader("customerCanPurchase", t("PLANS_COLUMN_BOOKABLE"))}
                                    {sortHeader("name", t("PLANS_COLUMN_NAME"))}
                                    <TableCell align="right">{t("PLANS_COLUMN_BASE_PER_MONTH")}</TableCell>
                                    <TableCell align="right">{t("PLANS_COLUMN_PER_EMPLOYEE")}</TableCell>
                                    <TableCell align="right" title={t("PLANS_SAMPLE_PRICE_EXPLANATION", {count: SAMPLE_EMPLOYEES})}>
                                        {t("PLANS_COLUMN_SAMPLE_PRICE", {count: SAMPLE_EMPLOYEES})}
                                    </TableCell>
                                    <TableCell sx={{width: 56}}>
                                        <Box component="span" sx={{position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)"}}>{t("PLANS_EXPAND_ROW_ARIA")}</Box>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading &&
                                        Array.from({length: 5}, (_, index) => (
                                                <TableRow key={index}>
                                                    <TableCell colSpan={6}><Skeleton height={24} /></TableCell>
                                                </TableRow>
                                        ))}
                                {!loading && filteredAndSorted.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{py: 4, color: colors.muted}}>
                                                {plans.length === 0 ? t("PLANS_EMPTY") : t("PLANS_NO_MATCH")}
                                            </TableCell>
                                        </TableRow>
                                )}
                                {filteredAndSorted.map((plan) => {
                                    const expanded = expandedId === plan.id;
                                    const toggle = () => setExpandedId((id) => (id === plan.id ? null : plan.id));
                                    const samplePrice = plan.monthlyBasePriceInCents + (plan.monthlyPricePerEmployeeInCents ?? 0) * SAMPLE_EMPLOYEES;
                                    return (
                                            <Fragment key={plan.id}>
                                                <TableRow
                                                        hover
                                                        onClick={toggle}
                                                        sx={{cursor: "pointer", "& > td": expanded ? {borderBottom: "none", backgroundColor: colors.rowHover} : undefined}}
                                                >
                                                    <TableCell>
                                                        {plan.customerCanPurchase === true && <StatusChip tone="success">{t("PLANS_PURCHASABLE_YES")}</StatusChip>}
                                                        {plan.customerCanPurchase === false && <StatusChip tone="neutral">{t("PLANS_PURCHASABLE_NO")}</StatusChip>}
                                                        {plan.customerCanPurchase === undefined && "-"}
                                                    </TableCell>
                                                    <TableCell sx={{fontWeight: 500, color: colors.ink}}>{plan.name}</TableCell>
                                                    <TableCell align="right">{formatEuroCents(plan.monthlyBasePriceInCents)}</TableCell>
                                                    <TableCell align="right">{hasEmployeePrice(plan) ? formatEuroCents(plan.monthlyPricePerEmployeeInCents ?? 0) : "-"}</TableCell>
                                                    <TableCell align="right">{hasEmployeePrice(plan) || plan.monthlyBasePriceInCents > 0 ? formatEuroCents(samplePrice) : "-"}</TableCell>
                                                    <TableCell sx={{py: 0}} onClick={(e) => e.stopPropagation()}>
                                                        <IconButton size="small" onClick={toggle} aria-expanded={expanded} aria-label={expanded ? t("PLANS_COLLAPSE_ARIA") : t("PLANS_EXPAND_ARIA")}>
                                                            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                                {expanded && (
                                                        <TableRow>
                                                            <TableCell colSpan={6} sx={{backgroundColor: colors.rowHover, pt: 0, pb: 2}}>
                                                                <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", md: "1fr 1fr"}, gap: 3}}>
                                                                    <Box>
                                                                        <Typography variant="overline" component="h3" sx={{color: colors.faint, display: "block"}}>{t("PLANS_INTERNAL_DESCRIPTION")}</Typography>
                                                                        <Typography variant="body1" sx={{color: colors.ink}}>{plan.internalDescription ?? "-"}</Typography>
                                                                    </Box>
                                                                    <Box>
                                                                        <Typography variant="overline" component="h3" sx={{color: colors.faint, display: "block"}}>{t("PLANS_CUSTOMER_DESCRIPTION")}</Typography>
                                                                        <Typography variant="body1" sx={{color: colors.ink}}>{plan.customerFacingDescription ?? "-"}</Typography>
                                                                    </Box>
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                )}
                                            </Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </>
    );
}
